import { useState, useRef, useEffect } from 'react';
import { useLanguage } from '../i18n';

interface DatePickerProps {
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  disabled?: boolean;
}

const MONTH_NAMES = {
  ko: ['1월', '2월', '3월', '4월', '5월', '6월', '7월', '8월', '9월', '10월', '11월', '12월'],
  en: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
};

const WEEKDAY_NAMES = {
  ko: ['일', '월', '화', '수', '목', '금', '토'],
  en: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
};

function DatePicker({ value, onChange, required, disabled = false }: DatePickerProps) {
  const { lang } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  
  // 현재 표시 중인 달
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());

  // 선택 가능 범위: 오늘 ~ 오늘+1달
  const minDate = new Date(today);
  const maxDate = new Date(today);
  maxDate.setMonth(maxDate.getMonth() + 1);

  // value에서 선택된 날짜 파싱
  const selectedDate = value ? new Date(value + 'T00:00:00') : null;

  // 클릭 외부 감지
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // 해당 월의 첫 날과 마지막 날
  const firstDayOfMonth = new Date(viewYear, viewMonth, 1);
  const lastDayOfMonth = new Date(viewYear, viewMonth + 1, 0);
  const startingDayOfWeek = firstDayOfMonth.getDay();
  const daysInMonth = lastDayOfMonth.getDate();

  // 이전 달 날짜들
  const prevMonthDays = [];
  const prevMonthLastDay = new Date(viewYear, viewMonth, 0).getDate();
  for (let i = startingDayOfWeek - 1; i >= 0; i--) {
    prevMonthDays.push(prevMonthLastDay - i);
  }

  // 현재 달 날짜들
  const currentMonthDays = [];
  for (let i = 1; i <= daysInMonth; i++) {
    currentMonthDays.push(i);
  }

  // 다음 달 날짜들 (6주 채우기)
  const totalCells = 42; // 6 rows * 7 days
  const nextMonthDays = [];
  const remainingCells = totalCells - prevMonthDays.length - currentMonthDays.length;
  for (let i = 1; i <= remainingCells; i++) {
    nextMonthDays.push(i);
  }

  // 날짜가 선택 가능한 범위 내인지 확인
  const isDateInRange = (day: number) => {
    const date = new Date(viewYear, viewMonth, day);
    date.setHours(0, 0, 0, 0);
    return date >= minDate && date <= maxDate;
  };

  const handleDateClick = (day: number) => {
    if (!isDateInRange(day)) return;
    const dateStr = `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    onChange(dateStr);
    setIsOpen(false);
  };

  // 이전 달로 이동 가능한지 확인
  const canGoPrev = () => {
    const prevMonth = viewMonth === 0 ? 11 : viewMonth - 1;
    const prevYear = viewMonth === 0 ? viewYear - 1 : viewYear;
    const lastDayOfPrevMonth = new Date(prevYear, prevMonth + 1, 0);
    return lastDayOfPrevMonth >= minDate;
  };

  // 다음 달로 이동 가능한지 확인
  const canGoNext = () => {
    const nextMonth = viewMonth === 11 ? 0 : viewMonth + 1;
    const nextYear = viewMonth === 11 ? viewYear + 1 : viewYear;
    const firstDayOfNextMonth = new Date(nextYear, nextMonth, 1);
    return firstDayOfNextMonth <= maxDate;
  };

  const goToPrevMonth = () => {
    if (!canGoPrev()) return;
    if (viewMonth === 0) {
      setViewYear(viewYear - 1);
      setViewMonth(11);
    } else {
      setViewMonth(viewMonth - 1);
    }
  };

  const goToNextMonth = () => {
    if (!canGoNext()) return;
    if (viewMonth === 11) {
      setViewYear(viewYear + 1);
      setViewMonth(0);
    } else {
      setViewMonth(viewMonth + 1);
    }
  };

  const goToToday = () => {
    setViewYear(today.getFullYear());
    setViewMonth(today.getMonth());
  };

  const isSelected = (day: number) => {
    if (!selectedDate) return false;
    return (
      selectedDate.getFullYear() === viewYear &&
      selectedDate.getMonth() === viewMonth &&
      selectedDate.getDate() === day
    );
  };

  const isToday = (day: number) => {
    return (
      today.getFullYear() === viewYear &&
      today.getMonth() === viewMonth &&
      today.getDate() === day
    );
  };

  // 표시용 날짜 포맷
  const displayValue = value
    ? lang === 'ko'
      ? value.replace(/-/g, '. ') + '.'
      : new Date(value + 'T00:00:00').toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
    : '';

  return (
    <div ref={containerRef} className="relative" data-testid="date-picker">
      <input
        type="text"
        value={displayValue}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        readOnly
        placeholder={lang === 'ko' ? '날짜 선택' : 'Select date'}
        className={`terminal-input ${disabled ? 'bg-terminal-input/60 cursor-not-allowed' : 'cursor-pointer'}`}
        required={required}
        disabled={disabled}
        data-testid="date-picker-input"
      />
      {/* Hidden input for form validation */}
      <input type="hidden" value={value} required={required} />
      
      {isOpen && !disabled && (
        <div className="absolute z-50 mt-1 border-2 border-black dark:border-[var(--neo-mint)] rounded-lg shadow-xl p-3 w-64 bg-white dark:bg-[#0a0a0a]" data-testid="date-picker-calendar">
          {/* 헤더 */}
          <div className="flex items-center justify-between mb-2">
            <button
              type="button"
              onClick={goToPrevMonth}
              disabled={!canGoPrev()}
              className={`p-1 ${canGoPrev() ? 'text-black/60 dark:text-white/60 hover:text-black dark:hover:text-white' : 'text-black/20 dark:text-white/20 cursor-not-allowed'}`}
              data-testid="date-picker-prev-month"
            >
              ←
            </button>
            <span className="text-black dark:text-white text-sm font-bold" data-testid="date-picker-month-year">
              {lang === 'ko' 
                ? `${viewYear}년 ${MONTH_NAMES.ko[viewMonth]}`
                : `${MONTH_NAMES.en[viewMonth]} ${viewYear}`
              }
            </span>
            <button
              type="button"
              onClick={goToNextMonth}
              disabled={!canGoNext()}
              className={`p-1 ${canGoNext() ? 'text-black/60 dark:text-white/60 hover:text-black dark:hover:text-white' : 'text-black/20 dark:text-white/20 cursor-not-allowed'}`}
              data-testid="date-picker-next-month"
            >
              →
            </button>
          </div>

          {/* 요일 헤더 */}
          <div className="grid grid-cols-7 gap-1 mb-1">
            {WEEKDAY_NAMES[lang].map((day) => (
              <div key={day} className="text-center text-black/50 dark:text-white/50 text-xs py-1 font-medium">
                {day}
              </div>
            ))}
          </div>

          {/* 날짜 그리드 */}
          <div className="grid grid-cols-7 gap-1">
            {/* 이전 달 */}
            {prevMonthDays.map((day, i) => (
              <div key={`prev-${i}`} className="text-center text-black/20 dark:text-white/20 text-xs py-1.5">
                {day}
              </div>
            ))}
            {/* 현재 달 */}
            {currentMonthDays.map((day) => {
              const inRange = isDateInRange(day);
              return (
                <button
                  key={day}
                  type="button"
                  onClick={() => handleDateClick(day)}
                  disabled={!inRange}
                  className={`
                    text-center text-xs py-1.5 rounded transition-colors
                    ${!inRange
                      ? 'text-black/20 dark:text-white/20 cursor-not-allowed'
                      : isSelected(day)
                        ? 'bg-[var(--neo-mint)] text-black font-bold'
                        : isToday(day)
                          ? 'border-2 border-[var(--neo-mint)] text-black dark:text-[var(--neo-mint)] font-bold'
                          : 'text-black dark:text-white hover:bg-[var(--neo-lime)] dark:hover:bg-white/10'
                    }
                  `}
                >
                  {day}
                </button>
              );
            })}
            {/* 다음 달 */}
            {nextMonthDays.map((day, i) => (
              <div key={`next-${i}`} className="text-center text-black/20 dark:text-white/20 text-xs py-1.5">
                {day}
              </div>
            ))}
          </div>

          {/* 푸터 */}
          <div className="flex justify-between mt-2 pt-2 border-t-2 border-black/20 dark:border-terminal-border">
            <button
              type="button"
              onClick={() => {
                onChange('');
                setIsOpen(false);
              }}
              className="text-status-danger text-xs font-bold hover:underline"
              data-testid="date-picker-clear"
            >
              {lang === 'ko' ? '삭제' : 'Clear'}
            </button>
            <button
              type="button"
              onClick={goToToday}
              className="text-[var(--neo-mint)] dark:text-status-go text-xs font-bold hover:underline"
              data-testid="date-picker-today"
            >
              {lang === 'ko' ? '오늘' : 'Today'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default DatePicker;
